import { useState } from "react";
import { Plane, Hotel, Bus, Car, FileText, ShieldCheck, Lock, Pencil, AlertTriangle, Info } from "lucide-react";
import styles from "./SeparateServices.module.css";
import EditVatSettingModal from "../../../../components/Finance/Payments/EditVatSettingModal";

const PRODUCT_ROWS = [
  {
    id: 1,
    product: "Flights",
    icon: "plane",
    handling: "VAT Exempt",
    handlingColor: "exempt",
    euVat: "0",
    nonEuVat: "0",
    euEditable: false,
    nonEuEditable: false,
    remarks: "Passenger transport exemption",
    details: "International passenger transport is exempt from VAT under EU directive. This applies regardless of destination.",
  },
  {
    id: 2,
    product: "Hotels",
    icon: "hotel",
    handling: "VAT by Location",
    handlingColor: "location",
    euVat: "Derived",
    nonEuVat: "Derived",
    euEditable: false,
    nonEuEditable: false,
    remarks: "Via Geo Data",
    details: "Hotel VAT is always determined by the country of accommodation. The rate is derived from Geo Data and cannot be overridden manually.",
  },
  {
    id: 3,
    product: "Transfers",
    icon: "bus",
    handling: "Normal VAT",
    handlingColor: "normal",
    euVat: "21",
    nonEuVat: "0",
    euEditable: true,
    nonEuEditable: true,
    remarks: "Place of supply rules",
    details: "VAT applied based on place of supply rules. EU transfers are subject to Belgian standard VAT rate.",
  },
  {
    id: 4,
    product: "Car Rental",
    icon: "car",
    handling: "Normal VAT",
    handlingColor: "normal",
    euVat: "21",
    nonEuVat: "0",
    euEditable: true,
    nonEuEditable: true,
    remarks: "Vehicle availability",
    details: "VAT applies based on where the vehicle is made available to the customer.",
  },
  {
    id: 5,
    product: "Visa",
    icon: "file",
    handling: "Administrative Service",
    handlingColor: "admin",
    euVat: "21",
    nonEuVat: "21",
    euEditable: true,
    nonEuEditable: true,
    remarks: "Belgian VAT",
    details: "Visa services are classified as administrative services. Belgian VAT rate applies regardless of destination.",
  },
];

const ICON_MAP = {
  plane: Plane,
  hotel: Hotel,
  bus: Bus,
  car: Car,
  file: FileText,
};

const formatValue = (val) => {
  if (!val && val !== 0 && val !== "0") return "—";
  if (val === "Derived") return "Derived";
  return `${val}%`;
};

const SeparateServices = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSetting, setEditSetting] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleEdit = (row, field) => {
    setEditSetting({
      ...row,
      field,
      euVat: row.euVat,
      nonEuVat: row.nonEuVat,
    });
    setShowEditModal(true);
  };

  const handleSave = (payload) => {
    console.log("Save Separate Service VAT:", editSetting, payload);
    setShowEditModal(false);
  };

  const toggleExpand = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleSection}>
          <div className={styles.title}>Separate Services</div>
          <span className={styles.subtitle}>VAT per Product and Destination — 7.4.2</span>
        </div>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>
          <ShieldCheck size={18} />
        </span>
        <div className={styles.infoContent}>
          <span className={styles.infoTitle}>System Enforcement — Binding</span>
          <span className={styles.infoText}>
            This table provides VAT parameters only. The VAT regime (TOMS, VAT_EXEMPT, NORMAL_VAT_BE, OUT_OF_SCOPE) remains system-derived
            and cannot be chosen manually. Parameters affect future calculations only.
          </span>
        </div>
      </div>

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Total Products</span>
          <span className={styles.summaryValue}>5</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Editable</span>
          <span className={styles.summaryValueGreen}>3</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>System-Derived</span>
          <span className={styles.summaryValueGray}>2</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>Excluded</span>
          <span className={styles.summaryValueRed}>1 (Insurance)</span>
        </div>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th></th>
                <th>PRODUCT TYPE</th>
                <th>VAT HANDLING TYPE</th>
                <th>EU VAT %</th>
                <th>NON-EU VAT %</th>
                <th>EU EDITABLE</th>
                <th>NON-EU EDITABLE</th>
                <th>REMARKS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {PRODUCT_ROWS.map((row) => {
                const IconComp = ICON_MAP[row.icon];
                const isExpanded = expandedRow === row.id;
                return (
                  <>
                    <tr key={row.id} className={isExpanded ? styles.expandedRow : ""}>
                      <td className={styles.iconCell}>
                        <span className={styles.productIcon}>
                          <IconComp size={16} />
                        </span>
                      </td>
                      <td className={styles.productName}>
                        <button className={styles.expandBtn} onClick={() => toggleExpand(row.id)}>
                          {row.product}
                          <Info size={13} className={styles.infoIconSmall} />
                        </button>
                      </td>
                      <td>
                        <span className={`${styles.handlingBadge} ${styles[row.handlingColor]}`}>
                          {row.handling}
                        </span>
                      </td>
                      <td className={styles.vatValue}>{formatValue(row.euVat)}</td>
                      <td className={styles.vatValue}>{formatValue(row.nonEuVat)}</td>
                      <td>
                        {row.euEditable ? (
                          <span className={styles.editableYes}>Yes</span>
                        ) : (
                          <span className={styles.editableNo}><Lock size={11} /> No</span>
                        )}
                      </td>
                      <td>
                        {row.nonEuEditable ? (
                          <span className={styles.editableYes}>Yes</span>
                        ) : (
                          <span className={styles.editableNo}><Lock size={11} /> No</span>
                        )}
                      </td>
                      <td className={styles.remarks}>{row.remarks}</td>
                      <td className={styles.rowActions}>
                        {row.euEditable && (
                          <button className={styles.editBtn} onClick={() => handleEdit(row, "EU")}>
                            <Pencil size={12} /> EU
                          </button>
                        )}
                        {row.nonEuEditable && (
                          <button className={styles.editBtn} onClick={() => handleEdit(row, "Non-EU")}>
                            <Pencil size={12} /> Non-EU
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${row.id}-detail`} className={styles.detailRow}>
                        <td colSpan={9}>
                          <div className={styles.detailContent}>
                            <span className={styles.detailText}>{row.details}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.ruleCards}>
        <div className={styles.ruleCardBlue}>
          <span className={styles.ruleIcon}>🏨</span>
          <div>
            <span className={styles.ruleTitle}>Hotel VAT — Binding Rule</span>
            <span className={styles.ruleText}>
              VAT is always determined by the hotel country via Geo Data. The rate is derived automatically
              and manual override is not allowed. This ensures compliance with EU place-of-supply rules
              for accommodation services.
            </span>
          </div>
        </div>
        <div className={styles.ruleCardRed}>
          <span className={styles.ruleIcon}>🛡️</span>
          <div>
            <span className={styles.ruleTitle}>Insurance — VAT Handling (Hard Rule)</span>
            <span className={styles.ruleText}>
              Insurance is completely excluded from VAT configuration. It is not a taxable service within
              the SUNSKY VAT engine. No VAT parameters can be set for insurance products.
            </span>
            <div className={styles.systemEnforced}>
              <AlertTriangle size={13} />
              <span>System-Enforced: VAT_REGIME = VAT_EXEMPT · VAT_RATE = 0% · VAT_BASE = 0 · VAT_AMOUNT = 0 · No override · No margin logic</span>
            </div>
          </div>
        </div>
      </div>

      <EditVatSettingModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        setting={editSetting}
        onSave={handleSave}
        saveLoading={false}
      />
    </div>
  );
};

export default SeparateServices;